import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { ChevronRight, Menu, Home as HomeIcon, FileText, Car, CalendarCheck, Phone, Clock, BellRing, Shield } from 'lucide-react';

const TopNavLinks = [
  { path: '/', label: 'Acasă', icon: HomeIcon },
  { path: '/permise', label: 'Permise de conducere', icon: FileText },
  { path: '/examinari', label: 'Examinări', icon: FileText },
  { path: '/inmatriculari', label: 'Înmatriculări', icon: Car },
  { path: '/rezervari', label: 'Rezervări on-line', icon: CalendarCheck },
  { path: '/program', label: 'Program cu publicul', icon: Clock },
  { path: '/noutati', label: 'Noutăți', icon: BellRing },
  { path: '/contact', label: 'Contact', icon: Phone },
];

const defaultSidebar = [
  { label: 'Acasă', path: '/' },
  { label: 'Permise de conducere', path: '/permise' },
  { label: 'Examinări', path: '/examinari' },
  { label: 'Înmatriculări', path: '/inmatriculari' },
  { label: 'Rezervări on-line', path: '/rezervari' },
  { label: 'Program cu publicul', path: '/program' },
  { label: 'Noutăți', path: '/noutati' },
  { label: 'Contact', path: '/contact' },
];

const SidebarMenus = {
  '/': defaultSidebar,
  '/program': defaultSidebar,
  '/contact': defaultSidebar,
  '/noutati': defaultSidebar,
  '/permise': [
    { label: 'Eliberare document prin procură', path: '/permise?modal=procura' },
    { label: 'Examen primul permis', path: '/permise?modal=examen_primul' },
    { label: 'Preschimbare model vechi', path: '/permise?modal=vechi' },
    { label: 'Preschimbare model nou', path: '/permise?modal=nou' },
    { label: 'Actualizare document model U.E.', path: '/permise?modal=ue' },
    { label: 'Preschimbare permis străin', path: '/permise?modal=strain' },
  ],
  '/examinari': [
    { label: 'Examinare pe calculator on-line', path: '/examinari?modal=online' },
    { label: 'Acte necesare', path: '/examinari?modal=acte' },
    { label: 'Săli de examinare', path: '/examinari?modal=sali' },
  ],
  '/inmatriculari': [
    { label: 'Înmatriculări permanente', path: '/inmatriculari?modal=permanente' },
    { label: 'Transcrierea de proprietate', path: '/inmatriculari?modal=transcriere' },
    { label: 'Înmatriculări temporare', path: '/inmatriculari?modal=temporare' },
    { label: 'Înmatriculări provizorii', path: '/inmatriculari?modal=provizorii' },
    { label: 'Înmatriculări pentru probe', path: '/inmatriculari?modal=probe' },
    { label: 'Radieri autovehicule', path: '/inmatriculari?modal=radieri' },
    { label: 'Preschimbări C.I.', path: '/inmatriculari?modal=preschimbari' },
  ],
  '/rezervari': [
    { label: 'Comandă plăcuțe numere', path: '/rezervari?service=Comandă plăcuțe numere' },
    { label: 'Programare la proba teoretică', path: '/rezervari?service=Programare proba teoretică (reducere suspendare)' },
    { label: 'Programare la proba practică', path: '/rezervari?service=Programare la proba practică' },
    // Dezactivate temporar:
    // { label: 'Înmatriculare / Transcriere', path: '/rezervari?service=Înmatriculare / Transcriere auto' },
    // { label: 'Preschimbare permis', path: '/rezervari?service=Preschimbare permis conducere' },
  ]
};

const MainLayout = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const currentSidebarMenu = SidebarMenus[currentPath] || SidebarMenus['/'];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      
      {/* Header oficial - ARANJAMENT MODERN + FUNDAL STEAG FLUTURÂND */}
      <header className="relative text-white shadow-lg overflow-hidden">
        
        {/* Fundalul animat cu steagul tricolor */}
        <div className="absolute inset-0 bg-waving-flag"></div>
        
        {/* Overlay întunecat pentru a asigura lizibilitatea textului alb */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"></div>
        
        {/* Steaua MAI in background subtil */}
        <div className="absolute left-10 top-1/2 -translate-y-1/2 opacity-15 pointer-events-none z-0">
          <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2">
            
            {/* Stanga: MAI */}
            <div className="text-center md:text-right flex-1">
              <h2 className="text-xs md:text-sm font-bold tracking-wide text-white uppercase drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">
                Ministerul Afacerilor Interne
              </h2>
            </div>
            
            {/* Centru: Stema Romaniei (SVG vectorial) */}
            <div className="shrink-0 flex flex-col items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_civgor-proto/artifacts/p0zo33qf_Coat_of_arms_of_Romania.svg" 
                alt="Stema României" 
                className="h-16 w-auto drop-shadow-[0_0_10px_rgba(255,255,255,0.3)] filter brightness-110"
              />
            </div>

            {/* Dreapta: Institutia Prefectului */}
            <div className="text-center md:text-left flex-1">
              <h2 className="text-xs md:text-sm font-bold tracking-wide text-white uppercase drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">
                Instituția Prefectului - Județul Gorj
              </h2>
            </div>
            
          </div>

          <div className="mt-1 text-center border-t border-white/20 pt-1">
            <h1 className="text-base md:text-lg font-extrabold text-white uppercase tracking-wider drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
              Serviciul Public Comunitar
            </h1>
            <h3 className="text-[10px] md:text-xs text-yellow-300 font-bold uppercase tracking-widest drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)] mt-0.5">
              Regim Permise de Conducere și Înmatriculare a Vehiculelor
            </h3>
          </div>
        </div>

        {/* Meniul principal de navigare */}
        <div className="bg-[#000d1e]/90 backdrop-blur-md border-b border-blue-900/50 relative z-10">
          <div className="max-w-7xl mx-auto px-2 lg:px-4">
            <nav className="flex flex-wrap justify-center w-full">
              {TopNavLinks.map((link) => {
                const isActive = currentPath === link.path;
                return (
                  <Link
                    key={link.path + link.label}
                    to={link.path}
                    className={`flex items-center gap-1.5 px-3 py-2 text-[10px] md:text-xs font-semibold transition-all uppercase tracking-wider text-center ${
                      isActive 
                      ? 'bg-blue-600/80 text-white shadow-[inset_0_-2px_0_0_rgba(250,204,21,1)]' 
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <link.icon className="w-3.5 h-3.5 hidden sm:block" />
                    <span>{link.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full flex flex-col md:flex-row gap-6">
        
        {/* Sidebar */}
        <aside className="w-full md:w-72 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden sticky top-6">
            <div className="bg-slate-100 p-4 border-b border-slate-200 flex items-center gap-2">
              <Menu className="w-5 h-5 text-blue-900" />
              <h3 className="font-bold text-blue-900 uppercase text-sm tracking-wider">Meniu Secțiune</h3>
            </div>
            <nav className="flex flex-col p-2">
              {currentSidebarMenu.map((item, index) => {
                const searchStr = location.search;
                const isActive = item.path.includes('?') 
                  ? (location.pathname + searchStr === item.path || searchStr === item.path.split('?')[1])
                  : (location.pathname === item.path && searchStr === '');
                
                return (
                  <Link
                    key={index}
                    to={item.path}
                    className={`flex items-center justify-between px-4 py-3.5 text-sm rounded-lg transition-colors group ${
                      isActive ? 'bg-blue-50 text-blue-700 font-bold border-l-4 border-blue-600' : 'text-slate-700 hover:bg-slate-50 hover:text-blue-700 font-medium'
                    }`}
                  >
                    <span>{item.label}</span>
                    <ChevronRight className={`w-4 h-4 transition-opacity ${isActive ? 'opacity-100 text-blue-600' : 'opacity-0 group-hover:opacity-100'}`} />
                  </Link>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Route */}
        <main className="flex-1 min-w-0 bg-white rounded-xl shadow-md border border-slate-200 p-5 md:p-6">
          <Outlet />
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-[#001B3A] text-slate-400 py-6 text-sm mt-auto border-t-4 border-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center">
          <p className="font-semibold text-slate-300">© {new Date().getFullYear()} Instituția Prefectului - Județul Gorj.</p>
          <p className="mb-4 text-xs text-slate-400 mt-1">Serviciul Public Comunitar Regim Permise de Conducere și Înmatriculare a Vehiculelor Gorj</p>
          <div className="flex flex-wrap justify-center gap-4 text-xs font-medium">
            <Link to="#" className="hover:text-yellow-400 transition-colors">Politica de confidențialitate</Link>
            <span className="text-slate-600">|</span>
            <Link to="/contact" className="hover:text-yellow-400 transition-colors">Contact</Link>
            <span className="text-slate-600">|</span>
            <Link to="/admin" className="hover:text-yellow-400 transition-colors">Autentificare Personal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;
